import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Users, Search } from "lucide-react";
import { SidebarGroupItem } from "./sidebarGroupItem.component";
import type { GroupSummary } from "../types";
import LoadingSpinner from "@/components/loadingSpinner.component";

interface GroupSidebarProps {
	groupSummaries: GroupSummary[];
	selectedGroupId: string | null;
	isLoading: boolean;
	onSearch: (search: string) => void;
	onSelectGroup: (groupId: string) => void;
}

/**
 * GroupSidebar component
 * @param groupSummaries - The group summaries
 * @param selectedGroupId - The selected group id
 * @param isLoading - The loading state
 * @param onSearch - The search function
 * @param onSelectGroup - The select group function
 * @param onEditGroup - The edit group function
 * @param onDeleteGroup - The delete group function
 */
export function GroupSidebar({
	groupSummaries,
	selectedGroupId,
	isLoading,
	onSearch,
	onSelectGroup,
}: GroupSidebarProps) {
	const [search, setSearch] = useState("");

	const handleSearchChange = (value: string) => {
		setSearch(value);
		onSearch(value);
	};

	// Filter the groups by searc
	const filteredGroups = groupSummaries.filter(
		(group) =>
			group.name.toLowerCase().includes(search.toLowerCase()) ||
			group.description?.toLowerCase().includes(search.toLowerCase()),
	);

	return (
		<div
			className={`${selectedGroupId ? "w-72" : "w-full"} transition-all duration-300 flex flex-col h-full`}
		>
			{/* Header Section */}
			<div className="p-4 flex-shrink-0">
				<div className="flex items-center justify-between">
					<div>
						<h1
							className={`${selectedGroupId ? "text-xl" : "text-3xl"} font-bold text-slate-900 transition-all`}
						>
							Korrektur-Gruppen
						</h1>
						{!selectedGroupId && (
							<p className="text-slate-600 mt-1">
								Verwalten Sie Gruppen für die Klausur-Korrektur
							</p>
						)}
					</div>
				</div>
			</div>

			{/* Combined Search & Group Items Section */}
			<div className="flex-1 mx-4 mb-4 bg-white rounded-lg border flex flex-col overflow-hidden">
				{/* Sticky Search Section */}
				<div className="p-4 flex-shrink-0 border-b border-slate-100">
					<div className="flex flex-col gap-3">
						<div className="flex gap-2">
							<div className="relative flex-1">
								<Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
								<Input
									placeholder="Gruppen suchen..."
									value={search}
									onChange={(e) => handleSearchChange(e.target.value)}
									className="pl-10 h-10 focus:border-blue-500 focus:ring-blue-500/20"
								/>
							</div>
						</div>
						{!selectedGroupId && (
							<div className="flex items-center justify-between text-sm">
								{groupSummaries.length > 0 && (
									<span className="text-slate-500 bg-slate-100 px-2 py-1 rounded-full text-xs">
										{groupSummaries.reduce(
											(acc, group) => acc + group.userCount,
											0,
										)}{" "}
										Nutzer*innen
									</span>
								)}
							</div>
						)}
					</div>
				</div>

				{/* Scrollable Group Items */}
				<div className="space-y-3 flex-1 overflow-auto px-4 pt-4 pb-2">
					{isLoading ? (
						<div className="flex flex-col items-center justify-center py-16 text-slate-500">
							<LoadingSpinner />
						</div>
					) : (
						<>
							{filteredGroups.map((group) => (
								<SidebarGroupItem
									key={group.id}
									group={group}
									isSelected={group.id === selectedGroupId}
									isMinimized={!!selectedGroupId}
									onSelect={() => onSelectGroup(group.id)}
								/>
							))}
							{filteredGroups.length === 0 && (
								<div className="flex flex-col items-center justify-center py-16 text-slate-500">
									<div className="bg-slate-100 rounded-full p-6 mb-6">
										<Users className="h-12 w-12 text-slate-400" />
									</div>
									<h3 className="text-lg font-semibold mb-2 text-slate-700">
										Keine Gruppen gefunden
									</h3>
									<p className="text-sm mb-6 text-center max-w-xs leading-relaxed">
										{search
											? "Keine Gruppen entsprechen der Suche. Verwenden Sie andere Suchbegriffe."
											: "Erstellen Sie eine erste Bewertungsgruppe, um mit der Korrektur zu beginnen."}
									</p>
								</div>
							)}
						</>
					)}
				</div>
			</div>
		</div>
	);
}
